import { useCallback, useMemo } from 'react';

import { SelectOption } from '../../../inputs/select-option';
import { Select } from '../../../internal';
import { DEFAULT_ITEMS_PER_PAGE_OPTIONS } from '../pagination-config';
import { usePaginationContext } from '../pagination-context';

import type { ReactElement } from 'react';

const PaginationItemsPerPageSelector = (): null | ReactElement => {
  const paginationContext = usePaginationContext();

  if (!paginationContext) {
    throw new Error('PaginationItemsPerPageSelector must be used within a PaginationContext.Provider');
  }

  const { itemsPerPage, itemsPerPageOptions, onItemsPerPageChange } = paginationContext;

  const options = useMemo(
    () =>
      (itemsPerPageOptions || DEFAULT_ITEMS_PER_PAGE_OPTIONS).map((item) => {
        if (typeof item === 'number') {
          return {
            label: item.toString(),
            value: item,
          };
        }

        return item;
      }),
    [itemsPerPageOptions],
  );

  const handleChange = useCallback(
    (_: unknown, value: null | number) => {
      if (value !== null && value !== undefined) {
        onItemsPerPageChange?.(value);
      }
    },
    [onItemsPerPageChange],
  );

  if (options.length < 2) {
    return null;
  }

  return (
    <Select
      aria-label="Items per page selector"
      className="rbui-pagination__items-per-page-selector"
      onChange={handleChange}
      value={itemsPerPage}
    >
      {options.map((option) => (
        <SelectOption key={option.value} value={option.value}>
          {option.label}
        </SelectOption>
      ))}
    </Select>
  );
};

PaginationItemsPerPageSelector.displayName = 'PaginationItemsPerPageSelector';

export { PaginationItemsPerPageSelector };
